"use client";

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "motion/react";
import { Icon } from "@iconify-icon/react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import ImageInput from "@/components/ImageInput";
import { axiosApiInstance } from "@/libs/axios";
import { API_ENDPOINTS } from "@/constants/api";
import { toast } from "@/utils/customToast";

type Props = {
  open: boolean;
  paymentId: string | null;
  onOpenChange: (open: boolean) => void;
  onSent: () => Promise<void> | void;
};

export default function StudentSendConfirmationDialog({ open, paymentId, onOpenChange, onSent }: Props) {
  const { t } = useTranslation(["finances", "common"]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [descLength, setDescLength] = useState(0);

  const formSchema = z.object({
    description: z
      .string()
      .min(
        1,
        t(
          "finances:studentPayments.confirmationForm.validation.descriptionRequired",
          "Description is required",
        ),
      )
      .max(
        1000,
        t(
          "finances:studentPayments.confirmationForm.validation.descriptionMax",
          "Max 1000 characters",
        ),
      ),
  });

  type FormValues = z.infer<typeof formSchema>;

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { description: "" },
    mode: "onTouched",
  });

  const handleSubmit = async (values: FormValues) => {
    if (!paymentId) return;
    if (!selectedFile) {
      toast.error(
        t(
          "finances:studentPayments.confirmationForm.validation.evidenceRequired",
          "Evidence image is required",
        ),
      );
      return;
    }
    const fd = new FormData();
    fd.append("evidence", selectedFile);
    fd.append("description", values.description);
    try {
      setIsSubmitting(true);
      await axiosApiInstance.post(API_ENDPOINTS.STUDENT.PAYMENTS.SEND_CONFIRMATION(paymentId), fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(t("finances:studentPayments.confirmation.success", "Confirmation sent"));
      onOpenChange(false);
      await onSent?.();
      form.reset();
      setSelectedFile(null);
      setDescLength(0);
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ||
          t("finances:studentPayments.confirmation.error", "Failed to send confirmation"),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onOpenChange(false);
      form.reset();
      setSelectedFile(null);
      setDescLength(0);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(o) : handleClose())}>
      <DialogContent
        aria-describedby={undefined}
        className="bg-background/75 text-accent-foreground max-h-[85dvh] max-w-2xl overflow-hidden rounded-2xl border-none p-0 shadow-2xl backdrop-blur-xl"
      >
        <ScrollArea className="relative max-h-[85dvh]">
          <motion.div
            animate={{ opacity: 1, y: 0 }}
            className="p-6"
            initial={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.3 }}
          >
            <DialogHeader className="pb-4">
              <motion.div
                animate={{ scale: 1 }}
                className="mx-auto mb-4 flex size-16 items-center justify-center"
                initial={{ scale: 0 }}
                transition={{ duration: 0.4, type: "spring", stiffness: 200 }}
              >
                <div className="flex size-full items-center justify-center rounded-full border-2 border-green-500/30 bg-green-500/20 backdrop-blur-sm">
                  <Icon className="text-green-500" height="1.5rem" icon="mdi:email-send" width="1.5rem" />
                </div>
              </motion.div>
              <DialogTitle className="text-accent-foreground text-center text-2xl font-bold">
                {t("finances:studentPayments.confirmationDialog.title", "Send confirmation")}
              </DialogTitle>
              <div className="text-muted-foreground space-y-1 text-center text-sm">
                <p>
                  {t(
                    "finances:studentPayments.confirmationDialog.desc1",
                    "Confirm that you have completed the required steps for this payment.",
                  )}
                </p>
                <p>
                  {t("finances:studentPayments.confirmationDialog.desc2", "Your tutor will be notified.")}
                </p>
              </div>
            </DialogHeader>

            <Form {...form}>
              <form className="space-y-6" onSubmit={form.handleSubmit(handleSubmit)}>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="bg-muted/30 flex h-8 w-8 items-center justify-center rounded-lg">
                      <Icon className="text-accent-foreground" height="1rem" icon="mdi:image" width="1rem" />
                    </div>
                    <h3 className="text-accent-foreground text-sm font-semibold">
                      {t("finances:studentPayments.confirmationForm.evidenceLabel", "Evidence image")}
                      <span className="ml-1 text-red-500">*</span>
                    </h3>
                  </div>
                  <Card className="border-input overflow-hidden rounded-xl border">
                    <ImageInput
                      maxSizeMB={10}
                      showDescription={false}
                      showFileRequirements={false}
                      onFileSelect={setSelectedFile}
                    />
                  </Card>
                  {!selectedFile && (
                    <p className="text-muted-foreground text-xs">
                      {t(
                        "finances:studentPayments.confirmationForm.validation.evidenceTypeSize",
                        "Allowed: jpg, jpeg, png, webp, gif, bmp, tiff; up to 10MB",
                      )}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="bg-muted/30 flex h-8 w-8 items-center justify-center rounded-lg">
                        <Icon className="text-accent-foreground" height="1rem" icon="tabler:notes" width="1rem" />
                      </div>
                      <h3 className="text-accent-foreground text-sm font-semibold">
                        {t("finances:studentPayments.confirmationForm.descriptionLabel", "Description")}
                        <span className="ml-1 text-red-500">*</span>
                      </h3>
                    </div>
                    <span className="text-muted-foreground text-sm">{descLength}/1000</span>
                  </div>
                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Card className="border-input overflow-hidden rounded-xl border shadow-sm">
                            <div className="p-0">
                              <ScrollArea className="border-input bg-background ring-offset-background focus-within:ring-ring h-32 w-full rounded-xl">
                                <Textarea
                                  className="[field-sizing:content] min-h-[128px] resize-none rounded-xl border-none! p-4 text-base break-words whitespace-pre-wrap focus-visible:ring-0 focus-visible:ring-offset-0"
                                  placeholder={t(
                                    "finances:studentPayments.confirmationForm.descriptionPlaceholder",
                                    "Describe your payment confirmation (e.g. transfer details)",
                                  )}
                                  {...field}
                                  maxLength={1000}
                                  onChange={(e) => {
                                    field.onChange(e.target.value);
                                    setDescLength(e.target.value.length);
                                  }}
                                />
                              </ScrollArea>
                            </div>
                          </Card>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="flex justify-center gap-4 pt-6">
                  <Button
                    className="bg-muted/80 hover:bg-muted text-muted-foreground hover:text-accent-foreground rounded-xl"
                    disabled={isSubmitting}
                    type="button"
                    variant="ghost"
                    onClick={handleClose}
                  >
                    {t("common:cancel", "Cancel")}
                  </Button>
                  <Button
                    className="min-w-32 gap-2 rounded-xl bg-green-500 text-white hover:bg-green-600"
                    disabled={isSubmitting || !selectedFile}
                    type="submit"
                  >
                    {isSubmitting ? (
                      <>
                        <Icon className="animate-spin" icon="lucide:loader-2" />
                        {t("common:processing", "Processing...")}
                      </>
                    ) : (
                      <>
                        <Icon icon="mdi:send-check" />
                        {t("finances:studentPayments.confirmationDialog.cta", "Send confirmation")}
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </Form>
          </motion.div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}


